import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, Compass, Loader2, Search, Sparkles, Video } from 'lucide-react'
import { api, type FrequencySummaryEntry, type User, type DiscoveredChannel } from '@/lib/api'
import { captureEvent, setPersonProperties } from '@/lib/analytics'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Checkbox } from '@/components/ui/checkbox'
import { Stepper, type StepKey } from '@/components/onboarding/Stepper'
import { ChannelPicker } from '@/components/onboarding/ChannelPicker'
import { QuotaMeter } from '@/components/onboarding/QuotaMeter'
import { AddChannelDialog } from '@/components/AddChannelDialog'

function formatSubs(n: number | null): string | null {
  if (n == null) return null
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M subscribers`
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K subscribers`
  return `${n} subscribers`
}

const PLAN_LIMITS: Record<string, { label: string; limit: number }> = {
  free: { label: 'Free', limit: 15 },
  plus: { label: 'Plus', limit: 150 },
  max: { label: 'Max', limit: 500 },
}

export function OnboardingPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState<StepKey>('intent')
  const [segment, setSegment] = useState<'creator' | 'viewer' | null>(null)
  const [importing, setImporting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [user, setUser] = useState<User | null>(null)
  // Returning users with any imported channels should never see the wizard —
  // they manage from /channels. Block render until we've confirmed they're new.
  const [eligibilityChecked, setEligibilityChecked] = useState(false)

  useEffect(() => {
    let cancelled = false
    Promise.all([api.getChannels({ pageSize: 1 }), api.getMe()])
      .then(([channels, me]) => {
        if (cancelled) return
        // Returning users with channels never see the wizard.
        if (channels.total > 0) {
          navigate('/channels', { replace: true })
          return
        }
        setUser(me.user)
        // The intent question is asked at most once PER ACCOUNT: user_segment is
        // persisted server-side, so it survives cookie clears, new browsers, and
        // new devices (they just re-login → same users row). If already set,
        // skip the intent step entirely.
        if (me.user.user_segment) {
          setSegment(me.user.user_segment)
          setStep('connect')
        }
        setEligibilityChecked(true)
      })
      .catch(() => {
        if (cancelled) return
        // On error, fall through to render — the import call itself will surface issues.
        setEligibilityChecked(true)
      })
    return () => { cancelled = true }
  }, [navigate])

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [originalFollowed, setOriginalFollowed] = useState<Set<string>>(new Set())
  const [frequency, setFrequency] = useState<Map<string, FrequencySummaryEntry>>(new Map())
  const [pendingCompute, setPendingCompute] = useState(0)
  const [importError, setImportError] = useState<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Niche discovery is a sub-mode of the (still step-1) connect view — keeping
  // the Stepper on "Connect" rather than adding a 4th linear step.
  const [connectMode, setConnectMode] = useState<'choices' | 'discover'>('choices')
  const [nicheDesc, setNicheDesc] = useState('')
  const [discovering, setDiscovering] = useState(false)
  const [discovered, setDiscovered] = useState<DiscoveredChannel[] | null>(null)
  const [discoverError, setDiscoverError] = useState<string | null>(null)
  const [discoverPicks, setDiscoverPicks] = useState<Set<string>>(new Set())
  // Set once the user adds at least one channel by URL on the connect step, so
  // we can offer a "Continue" path without forcing a subscription import.
  const [addedViaUrl, setAddedViaUrl] = useState(0)


  const refreshFrequency = async () => {
    try {
      const res = await api.getFrequencySummary()
      const map = new Map<string, FrequencySummaryEntry>()
      for (const c of res.channels) map.set(c.id, c)
      setFrequency(map)
      setPendingCompute(res.pending)
      return res.pending
    } catch (err) {
      console.error(err)
      return 0
    }
  }

  // While on the choose step, poll the frequency endpoint until all channels
  // have been measured. Polling stops automatically once `pending` hits 0.
  useEffect(() => {
    if (step !== 'choose') return
    refreshFrequency()
    pollRef.current = setInterval(async () => {
      const remaining = await refreshFrequency()
      if (remaining === 0 && pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }, 3000)
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [step])

  const handleImport = async () => {
    setImporting(true)
    setImportError(null)
    try {
      await api.importSubscriptions()
      // Pre-select already-followed channels using the lightweight ids endpoint
      // so we don't get capped by pagination for power users.
      const { ids } = await api.getFollowedChannelIds()
      const followed = new Set(ids)
      setSelected(new Set(followed))
      setOriginalFollowed(followed)
      setStep('choose')
    } catch (err) {
      console.error(err)
      const msg = err instanceof Error ? err.message : 'Something went wrong importing your subscriptions.'
      setImportError(msg)
    } finally {
      setImporting(false)
    }
  }

  const chooseSegment = (seg: 'creator' | 'viewer') => {
    setSegment(seg)
    setStep('connect')
    // Best-effort: persist to the user record (server-side tailoring) + set a
    // PostHog person property (cohort analytics). Never block the flow.
    api.setSegment(seg).catch(() => {})
    setPersonProperties({ user_segment: seg })
  }

  // Skipping still PERSISTS a segment ('viewer' = default, non-creator branch)
  // so the intent step is shown at most once per account and never re-appears.
  // A distinct event keeps skippers separable from explicit viewers in analytics.
  const skipIntent = () => {
    setSegment('viewer')
    setStep('connect')
    api.setSegment('viewer').catch(() => {})
    setPersonProperties({ user_segment: 'viewer' })
    captureEvent('onboarding_segment_skipped', {})
  }

  // Seed the choose step's selection from the server (channels already followed,
  // e.g. via add-by-URL or discovery) and advance.
  const continueFromConnect = async () => {
    const { ids } = await api.getFollowedChannelIds()
    const followed = new Set(ids)
    setSelected(new Set(followed))
    setOriginalFollowed(followed)
    setStep('choose')
  }

  const runDiscovery = async () => {
    const desc = nicheDesc.trim()
    if (!desc || discovering) return
    setDiscovering(true)
    setDiscoverError(null)
    try {
      const { channels } = await api.discoverNiche(desc)
      setDiscovered(channels)
      // Pre-select the suggestions the user isn't already following — results
      // are ranked, and following many channels is cheap (quota gates summaries,
      // not follows). They can deselect any before adding.
      setDiscoverPicks(new Set(channels.filter((c) => !c.already_followed).map((c) => c.id)))
    } catch (err) {
      setDiscoverError(err instanceof Error ? err.message : "Couldn't search your niche right now.")
    } finally {
      setDiscovering(false)
    }
  }

  const addDiscovered = async () => {
    const ids = Array.from(discoverPicks)
    setSaving(true)
    try {
      if (ids.length > 0) await api.followChannels(ids)
      // Hand off to the choose step (seed selection from the server) so they can
      // still refine or add their subscriptions on top.
      const { ids: followedIds } = await api.getFollowedChannelIds()
      const followed = new Set(followedIds)
      setSelected(new Set(followed))
      setOriginalFollowed(followed)
      setStep('choose')
    } catch (err) {
      setDiscoverError(err instanceof Error ? err.message : 'Something went wrong adding those channels.')
    } finally {
      setSaving(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const toFollow: string[] = []
      const toUnfollow: string[] = []
      for (const id of selected) if (!originalFollowed.has(id)) toFollow.push(id)
      for (const id of originalFollowed) if (!selected.has(id)) toUnfollow.push(id)

      if (toFollow.length > 0) await api.followChannels(toFollow)
      if (toUnfollow.length > 0) await api.unfollowChannels(toUnfollow)

      setStep('done')
    } catch (err) {
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  const planKey = (user?.billing_plan as keyof typeof PLAN_LIMITS) || 'free'
  const plan = PLAN_LIMITS[planKey] || PLAN_LIMITS.free

  if (!eligibilityChecked) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-3xl py-8">
      <div className="mb-8 flex justify-center">
        <Stepper current={step} />
      </div>

      {step === 'intent' && (
        <div className="mx-auto max-w-md text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-accent">
            <Sparkles className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold">What brings you to Rundown?</h1>
          <p className="mt-2 text-muted-foreground">
            We'll tailor your setup. You can change how you use Rundown anytime.
          </p>
          <div className="mt-8 grid gap-3 text-left">
            <button
              type="button"
              onClick={() => chooseSegment('creator')}
              className="group rounded-xl border p-5 transition-colors hover:border-primary hover:bg-accent/40"
            >
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Video className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <p className="font-semibold">I create YouTube videos</p>
                  <p className="text-sm text-muted-foreground">Track your niche and competitors</p>
                </div>
                <ArrowRight className="ml-auto h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
              </div>
            </button>
            <button
              type="button"
              onClick={() => chooseSegment('viewer')}
              className="group rounded-xl border p-5 transition-colors hover:border-primary hover:bg-accent/40"
            >
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Compass className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <p className="font-semibold">I'm here to keep up &amp; learn</p>
                  <p className="text-sm text-muted-foreground">Summaries of the channels you follow</p>
                </div>
                <ArrowRight className="ml-auto h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
              </div>
            </button>
          </div>
          <button
            type="button"
            onClick={skipIntent}
            className="mt-5 text-sm text-muted-foreground hover:text-foreground"
          >
            I'll decide later
          </button>
        </div>
      )}

      {step === 'connect' && connectMode === 'choices' && (
        <div className="mx-auto max-w-md text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-accent">
            <Sparkles className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold">{segment === 'creator' ? 'Build your niche' : 'Add your channels'}</h1>
          <p className="mt-2 text-muted-foreground">
            {segment === 'creator'
              ? 'Add the channels in your space — competitors and benchmarks included. Nothing is summarized yet; you’ll confirm on the next step.'
              : 'Bring in the channels you follow. Nothing is summarized yet; you’ll confirm on the next step.'}
          </p>
          <Button size="lg" className="mt-8 w-full gap-2" onClick={handleImport} disabled={importing}>
            {importing && <Loader2 className="h-4 w-4 animate-spin" />}
            {importing ? 'Importing…' : 'Import my YouTube subscriptions'}
          </Button>
          <div className="mt-3">
            <AddChannelDialog
              onAdded={(channel) => {
                setSelected((prev) => new Set(prev).add(channel.id))
                setAddedViaUrl((n) => n + 1)
              }}
              trigger={
                <Button variant="outline" size="lg" className="w-full">
                  Add a channel by URL / @handle
                </Button>
              }
            />
          </div>

          {addedViaUrl > 0 && (
            <Button variant="ghost" className="mt-3 w-full gap-2" onClick={continueFromConnect}>
              Continue with {addedViaUrl} channel{addedViaUrl === 1 ? '' : 's'}
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}

          {importError && (
            <div className="mt-6 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-left text-sm text-destructive">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p className="font-medium">Import failed</p>
                <p className="mt-0.5 text-destructive/80">{importError}</p>
              </div>
            </div>
          )}

          {segment === 'creator' && (
            <div className="mt-8 border-t pt-5">
              <p className="text-sm text-muted-foreground">
                Not sure who's in your niche?{' '}
                <button
                  type="button"
                  onClick={() => setConnectMode('discover')}
                  className="font-medium text-primary underline-offset-4 hover:underline"
                >
                  Describe it and we'll find them →
                </button>
              </p>
            </div>
          )}
        </div>
      )}

      {step === 'connect' && connectMode === 'discover' && (
        <div className="mx-auto max-w-xl">
          <button
            type="button"
            onClick={() => setConnectMode('choices')}
            className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <h1 className="text-2xl font-bold">Describe your niche</h1>
          <p className="mt-2 text-muted-foreground">
            Tell us what you make videos about and we'll find the channels in your space — competitors
            and benchmarks you'd want to track.
          </p>

          <textarea
            value={nicheDesc}
            onChange={(e) => { setNicheDesc(e.target.value); setDiscoverError(null) }}
            maxLength={500}
            rows={3}
            placeholder="e.g. self-hosting and homelab tutorials for developers"
            className="mt-5 w-full resize-none rounded-xl border border-input bg-background px-4 py-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <Button className="mt-3 gap-2" onClick={runDiscovery} disabled={discovering || !nicheDesc.trim()}>
            {discovering ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            {discovering ? 'Finding channels…' : 'Find channels'}
          </Button>

          {discoverError && (
            <div className="mt-4 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{discoverError}</p>
            </div>
          )}

          {discovered && discovered.length === 0 && !discovering && (
            <p className="mt-6 text-sm text-muted-foreground">
              No channels found for that description. Try different wording, or{' '}
              <button type="button" onClick={() => setConnectMode('choices')} className="font-medium text-primary hover:underline">
                add them by URL instead
              </button>.
            </p>
          )}

          {discovered && discovered.length > 0 && (
            <div className="mt-6">
              <div className="mb-3 flex items-center justify-between">
                <p className="text-sm text-muted-foreground">{discovered.length} channels found · {discoverPicks.size} selected</p>
                <button
                  type="button"
                  className="text-sm font-medium text-primary hover:underline"
                  onClick={() =>
                    setDiscoverPicks((prev) =>
                      prev.size === discovered.length ? new Set() : new Set(discovered.map((c) => c.id))
                    )
                  }
                >
                  {discoverPicks.size === discovered.length ? 'Clear all' : 'Select all'}
                </button>
              </div>
              <ul className="divide-y rounded-xl border">
                {discovered.map((c) => {
                  const subs = formatSubs(c.subscriber_count)
                  const checked = discoverPicks.has(c.id)
                  return (
                    <li key={c.id}>
                      <label className="flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-muted/40">
                        <Checkbox
                          checked={checked}
                          onCheckedChange={(v) =>
                            setDiscoverPicks((prev) => {
                              const next = new Set(prev)
                              if (v) next.add(c.id); else next.delete(c.id)
                              return next
                            })
                          }
                        />
                        <Avatar className="h-9 w-9">
                          <AvatarImage src={c.thumbnail_url} alt={c.title} />
                          <AvatarFallback>{c.title.slice(0, 2).toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{c.title}</p>
                          <p className="truncate text-xs text-muted-foreground">
                            {subs}{subs && c.already_followed ? ' · ' : ''}{c.already_followed ? 'Already following' : ''}
                          </p>
                        </div>
                      </label>
                    </li>
                  )
                })}
              </ul>
              <Button className="mt-5 w-full gap-2" onClick={addDiscovered} disabled={saving || discoverPicks.size === 0}>
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Add {discoverPicks.size} channel{discoverPicks.size === 1 ? '' : 's'} &amp; continue
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}

      {step === 'choose' && (
        <div className="space-y-5">
          <div>
            <h1 className="text-2xl font-bold">Confirm your channels</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              These are the channels in your niche. Toggle any off, or add more — Rundown
              summarizes every new upload and briefs you on what your niche is saying.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <span>Tracking a competitor who isn&rsquo;t in your subscriptions?</span>
              <AddChannelDialog
                onAdded={(channel) => setSelected((prev) => new Set(prev).add(channel.id))}
                trigger={
                  <Button variant="link" size="sm" className="h-auto p-0 text-sm">
                    Add them by URL
                  </Button>
                }
              />
            </div>
          </div>

          <QuotaMeter
            selectedIds={selected}
            frequency={frequency}
            planLabel={plan.label}
            planLimit={plan.limit}
            pendingCompute={pendingCompute}
          />

          <ChannelPicker
            selected={selected}
            onSelectionChange={setSelected}
          />

          <div className="flex items-center justify-between gap-3 border-t pt-4">
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{selected.size}</span> channel
              {selected.size === 1 ? '' : 's'} selected
            </p>
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => setStep('connect')} disabled={saving}>
                Back
              </Button>
              <Button onClick={handleSave} disabled={saving || selected.size === 0} className="gap-1.5">
                {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Continue
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {step === 'done' && (
        <Card className="mx-auto max-w-md p-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
            <CheckCircle2 className="h-7 w-7 text-emerald-600" />
          </div>
          <h1 className="text-xl font-bold">You're all set</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            We'll start summarizing the most recent video from each channel you followed. New
            uploads will appear in your feed as they're published.
          </p>
          <Button size="lg" className="mt-6 gap-1.5" onClick={() => navigate('/feed')}>
            Go to my feed
            <ArrowRight className="h-4 w-4" />
          </Button>
        </Card>
      )}
    </div>
  )
}
